import { captureErrorLog, captureLog, captureStep } from "./captureDiagnostics";
import { type CaptureTelemetry, measureCaptureStep } from "./captureTiming";

const PARAM = "__linkhub_capture";
declare const __XVIEW_PERF_VERSION__: string;
declare const __XVIEW_PERF_LOCAL_CAPTURE__: boolean;

export interface CaptureOptions {
  endpoint: string;
  token: string;
  pageUrl: string;
  telemetry?: CaptureTelemetry;
}

type CaptureWindow = Window & {
  xviewPersonalCapture?: { runCapture(options: CaptureOptions): Promise<void> };
};

export function parseCaptureRequest(href: string) {
  const url = new URL(href);
  const marker = url.searchParams.get(PARAM);
  const match = marker?.match(/^(?:(prod)\.)?(kr|ca|jp)\.([a-f0-9]{64})$/);
  if (!match || match[0] !== marker || url.protocol !== "https:") return null;
  url.searchParams.delete(PARAM);
  return {
    endpoint: `https://linkhub.${match[1] ? "" : "alpha."}${match[2]}.wekarrot.net/api/page-capture/ingest`,
    token: match[3],
    pageUrl: url.href,
  };
}

async function postJson(endpoint: string, body: unknown) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 10000);
  try {
    const response = await fetch(endpoint, {
      method: "POST",
      credentials: "omit",
      referrerPolicy: "no-referrer",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    captureLog("api.response", {
      status: response.status,
      requestId: response.headers.get("X-LinkHub-Capture-Request-Id"),
    });
    return response;
  } finally {
    clearTimeout(timeout);
  }
}

function loadCaptureBundle(nonce: string) {
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    // Versioned sibling release: never accept a script URL from the query or API.
    script.src =
      typeof __XVIEW_PERF_LOCAL_CAPTURE__ !== "undefined" &&
      __XVIEW_PERF_LOCAL_CAPTURE__
        ? "/vendor/capture.iife.js"
        : `https://assets.krrt.io/daangn/xview-perf/${__XVIEW_PERF_VERSION__}/dist/capture.iife.js`;
    captureLog("bundle.source", { source: script.src });
    script.async = true;
    script.referrerPolicy = "no-referrer";
    if (nonce) script.nonce = nonce;
    const finish = (error?: Error) => {
      clearTimeout(timeout);
      script.onload = null;
      script.onerror = null;
      script.remove();
      error ? reject(error) : resolve();
    };
    const timeout = setTimeout(
      () => finish(new Error("bundle_load_failed")),
      10000,
    );
    script.onload = () => finish();
    script.onerror = () => finish(new Error("bundle_load_failed"));
    document.head.appendChild(script);
  });
}

function showCaptureFailure(error: unknown, claimConnectionFailed = false) {
  const reason = error instanceof Error ? error.message : "";
  const detail = claimConnectionFailed
    ? "캡처 서버에 연결하지 못했습니다. 사내망에 연결되어 있지 않아 발생했을 수 있습니다. 사내 Wi-Fi 또는 VPN 연결을 확인해 주세요. 네트워크 상태나 페이지 보안 정책으로도 연결이 차단될 수 있습니다."
    : reason === "claim_invalid_response"
      ? "캡처 서버에서 올바르지 않은 응답을 받았습니다. 문제가 계속되면 LinkHub 담당자에게 문의해 주세요."
      : reason === "claim_rejected"
        ? "캡처 요청을 사용할 수 없습니다. 요청이 만료되었거나 이미 사용되었을 수 있습니다."
        : reason === "page_changed"
          ? "캡처 중 페이지가 변경되었습니다. 원하는 화면에서 다시 시도해 주세요."
          : reason === "bundle_load_failed"
            ? "캡처 기능을 불러오지 못했습니다. 네트워크 연결이나 페이지 보안 정책을 확인해 주세요."
            : "화면 캡처 또는 이미지 업로드를 완료하지 못했습니다. 네트워크 연결과 LinkHub의 요청 상태를 확인해 주세요.";
  try {
    window.alert(
      `화면 캡처 중 오류가 발생했습니다.\n\n${detail}\n\nLinkHub에서 새 캡처 요청을 만든 뒤 새 QR로 다시 시도해 주세요.`,
    );
  } catch (alertError) {
    // Some hosts disable dialogs. Notification failures must not escape the SDK.
    captureErrorLog("failure.alert.failed", alertError);
  }
}

// Returns synchronously so capture sessions never enter ordinary performance tracking.
export function startPersonalCapture(): boolean {
  let accepted = false;
  try {
    const markerPresent = new URL(location.href).searchParams.has(PARAM);
    captureLog("entry", { markerPresent });
    const request = parseCaptureRequest(location.href);
    if (!request) {
      captureLog("capture.skipped", {
        reason: markerPresent ? "invalid_marker_or_protocol" : "no_marker",
      });
      return false;
    }
    accepted = true;
    captureLog("marker.accepted");
    const nonce =
      (document.currentScript as HTMLScriptElement | null)?.nonce ?? "";
    captureLog("marker.remove.started");
    history.replaceState(history.state, "", request.pageUrl);
    captureLog("marker.remove.completed");
    void (async () => {
      let uploadToken: string | undefined;
      let claimResponseReceived = false;
      let telemetry: CaptureTelemetry | undefined;
      try {
        const response = await captureStep("claim", () =>
          postJson(request.endpoint, {
            token: request.token,
            url: request.pageUrl,
          }),
        );
        claimResponseReceived = true;
        if (!response.ok) {
          captureLog("claim.rejected", { status: response.status });
          throw new Error("claim_rejected");
        }
        let body: unknown;
        try {
          body = await response.json();
        } catch (cause) {
          captureErrorLog("claim.invalid_response", cause);
          throw Object.assign(new Error("claim_invalid_response"), { cause });
        }
        if (
          body === null ||
          typeof body !== "object" ||
          !("uploadToken" in body) ||
          typeof body.uploadToken !== "string" ||
          !/^[a-f0-9]{64}$/.test(body.uploadToken)
        ) {
          captureLog("claim.invalid_response");
          throw new Error("claim_invalid_response");
        }
        captureLog("claim.accepted");
        const claimedToken = body.uploadToken;
        uploadToken = claimedToken;
        if ("captureTimingVersion" in body && body.captureTimingVersion === 1)
          telemetry = { timing: {} };
        await measureCaptureStep(
          telemetry,
          "bundle_load",
          "bundle_load_ms",
          () => captureStep("bundle.load", () => loadCaptureBundle(nonce)),
        );
        const capture = (window as CaptureWindow).xviewPersonalCapture;
        if (!capture) throw new Error("bundle_load_failed");
        await captureStep("capture.run", () =>
          capture.runCapture({
            ...request,
            token: claimedToken,
            ...(telemetry ? { telemetry } : {}),
          }),
        );
      } catch (error) {
        captureErrorLog("capture.failed", error, {
          phase: uploadToken ? "after_claim" : "before_claim",
        });
        showCaptureFailure(error, !claimResponseReceived);
        // Detailed errors stay in local diagnostics. CSP can block this report;
        // LinkHub independently expires the request in that case.
        if (uploadToken) {
          const reason =
            error instanceof Error &&
            ["bundle_load_failed", "page_changed", "upload_failed"].includes(
              error.message,
            )
              ? error.message
              : "capture_failed";
          captureLog("failure.reason", { reason });
          await captureStep("failure.report", async () => {
            const response = await postJson(request.endpoint, {
              token: uploadToken,
              error: reason,
              ...(telemetry
                ? {
                    timing: telemetry.timing,
                    failure_stage: telemetry.failureStage,
                  }
                : {}),
            });
            if (!response.ok) throw new Error("failure_report_rejected");
          }).catch(() => {});
        }
      }
    })();
    return true;
  } catch (error) {
    captureErrorLog("entry.failed", error);
    if (accepted) showCaptureFailure(error);
    return false;
  }
}
